import React, { useState, useEffect, useMemo } from 'react';
import { bookings } from '../../api';
import SlotCourtInfo from './SlotCourtInfo';
import './Bookings.css';

/**
 * Coach / headcoach: push open slots to students.
 *  1. pick the slots to advertise
 *  2. pick who gets it: all my students, students inactive 30+ days, students waiting for that
 *     lesson type, hand-picked students (one, many) - and, for a headcoach, every player
 *  3. optional short message; optionally also feature the slots in the students' in-app banner
 */
const AUDIENCES = [
  { key: 'my-students', label: 'All my students' },
  { key: 'dormant', label: 'Students inactive for 30+ days' },
  { key: 'waitlist', label: 'Students waiting for this lesson type' },
  { key: 'selected', label: 'Choose students…' },
  { key: 'all', label: 'Every player on the app', headcoachOnly: true }
];

const fmt = (d) => new Date(d).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

export default function AdvertiseSlotsModal({ isOpen, onClose, slots = [], students = [], onDone }) {
  const openSlots = useMemo(() => slots
    .filter(s => s.status === 'available' && !(s.currentBookings > 0) && new Date(s.startTime) > new Date())
    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime)), [slots]);

  const [slotIds, setSlotIds] = useState([]);
  const [audience, setAudience] = useState('my-students');
  const [playerIds, setPlayerIds] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [message, setMessage] = useState('');
  const [feature, setFeature] = useState(true);
  const [counts, setCounts] = useState({});
  const [totals, setTotals] = useState({});
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Start with this week's open slots ticked
  useEffect(() => {
    if (!isOpen) return;
    const weekAhead = Date.now() + 7 * 24 * 3600 * 1000;
    setSlotIds(openSlots.filter(s => new Date(s.startTime).getTime() <= weekAhead).map(s => s._id));
    setResult(null);
    setError('');
  }, [isOpen, openSlots]);

  // How many students each audience would reach, for the ticked slots
  useEffect(() => {
    if (!isOpen || slotIds.length === 0) { setCounts({}); setTotals({}); return undefined; }
    let cancelled = false;
    const t = setTimeout(() => {
      bookings.getAdvertiseAudiences(slotIds)
        .then(res => { if (!cancelled) { setCounts(res.data.counts || {}); setTotals(res.data.totals || {}); } })
        .catch(() => { if (!cancelled) { setCounts({}); setTotals({}); } });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [isOpen, slotIds]);

  if (!isOpen) return null;

  const toggle = (list, setList, id) => setList(list.includes(id) ? list.filter(x => x !== id) : [...list, id]);
  const visibleStudents = [...students]
    .filter(s => (s.name || '').toLowerCase().includes(studentSearch.toLowerCase()))
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));

  const reach = audience === 'selected' ? playerIds.length : counts[audience];
  const canSend = !sending && slotIds.length > 0 && (audience !== 'selected' || playerIds.length > 0);

  const handleSend = async () => {
    setSending(true);
    setError('');
    try {
      const res = await bookings.advertiseSlots({ slotIds, audience, message, playerIds, feature });
      setResult(res.data);
      if (onDone) onDone();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="booking-modal-overlay" onClick={onClose}>
      <div className="booking-modal-content" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="booking-modal-header">
          <h2>📣 Advertise open slots</h2>
          <button className="booking-modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {result ? (
          <div style={{ padding: 20 }}>
            <p style={{ fontSize: 15, fontWeight: 700, marginTop: 0 }}>
              ✅ Sent to {result.sent} student{result.sent !== 1 ? 's' : ''}
            </p>
            {(result.optedOut > 0 || result.noToken > 0 || result.failed > 0) && (
              <p style={{ fontSize: 13, color: '#7f8c8d' }}>
                {[result.optedOut > 0 && `${result.optedOut} turned slot promotions off`,
                  result.noToken > 0 && `${result.noToken} can't receive notifications yet`,
                  result.failed > 0 && `${result.failed} failed to send`].filter(Boolean).join(' · ')}
              </p>
            )}
            {feature && <p style={{ fontSize: 13, color: '#7f8c8d' }}>The slots are also featured in your students' Coaching tab.</p>}
            <button className="booking-btn-primary" onClick={onClose}>Done</button>
          </div>
        ) : (
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* 1. slots */}
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>1. Slots ({slotIds.length} selected)</div>
              {openSlots.length === 0 ? (
                <div style={{ fontSize: 13, color: '#7f8c8d' }}>You have no open, unbooked slots to advertise.</div>
              ) : (
                <div style={{ maxHeight: 190, overflowY: 'auto', border: '1px solid #e3e6ee', borderRadius: 8 }}>
                  {openSlots.map(s => (
                    <label key={s._id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 10px', borderBottom: '1px solid #f0f2f8', cursor: 'pointer' }}>
                      <input type="checkbox" checked={slotIds.includes(s._id)} onChange={() => toggle(slotIds, setSlotIds, s._id)} style={{ marginTop: 3 }} />
                      <span style={{ fontSize: 13 }}>
                        <strong>{fmt(s.startTime)}</strong> · {Math.round((new Date(s.endTime) - new Date(s.startTime)) / 60000)} min
                        {s.featured && <span style={{ marginLeft: 6 }}>⭐</span>}
                        <SlotCourtInfo slot={s} margin="2px 0 0" />
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* 2. audience */}
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>2. Who should get it?</div>
              {AUDIENCES.filter(a => !a.headcoachOnly || counts.all !== undefined).map(a => (
                <label key={a.key} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '5px 0', fontSize: 13, cursor: 'pointer' }}>
                  <input type="radio" name="ad-audience" checked={audience === a.key} onChange={() => setAudience(a.key)} />
                  {a.label}
                  {a.key !== 'selected' && counts[a.key] !== undefined && (
                    <span style={{ color: '#7f8c8d' }} title="Students who can receive a notification, out of everyone in this group">
                      ({counts[a.key]}{totals[a.key] !== undefined ? ` of ${totals[a.key]}` : ''})
                    </span>
                  )}
                </label>
              ))}

              {audience === 'selected' && (
                <div style={{ marginTop: 8 }}>
                  <input
                    type="text"
                    placeholder="Search students…"
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, marginBottom: 6 }}
                  />
                  <div style={{ maxHeight: 150, overflowY: 'auto', border: '1px solid #e3e6ee', borderRadius: 8 }}>
                    {visibleStudents.map(s => (
                      <label key={s._id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '7px 10px', fontSize: 13, cursor: 'pointer' }}>
                        <input type="checkbox" checked={playerIds.includes(s._id)} onChange={() => toggle(playerIds, setPlayerIds, s._id)} />
                        {s.name}
                      </label>
                    ))}
                    {visibleStudents.length === 0 && <div style={{ padding: 10, fontSize: 13, color: '#7f8c8d' }}>No students found</div>}
                  </div>
                </div>
              )}
            </div>

            {/* 3. message */}
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>3. Message (optional)</div>
              <textarea
                value={message}
                maxLength={140}
                rows={2}
                placeholder="e.g. Court 3 is free, bring a friend!"
                onChange={(e) => setMessage(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, fontFamily: 'inherit' }}
              />
              <div style={{ fontSize: 11, color: '#7f8c8d', textAlign: 'right' }}>{message.length}/140</div>
              {audience !== 'selected' && totals[audience] > (counts[audience] || 0) && (
                <div style={{ fontSize: 12, color: '#92400e', background: '#fff7e0', borderRadius: 6, padding: '6px 8px', marginBottom: 6 }}>
                  {totals[audience] - (counts[audience] || 0)} of {totals[audience]} students can't get a notification (they haven't enabled push, or turned slot alerts off). Keep the box below ticked so they still see these slots in their Coaching tab.
                </div>
              )}
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginTop: 4, cursor: 'pointer' }}>
                <input type="checkbox" checked={feature} onChange={(e) => setFeature(e.target.checked)} />
                Also feature these slots in my students' Coaching tab
              </label>
            </div>

            {error && <div style={{ color: '#c0392b', fontSize: 13 }}>⚠️ {error}</div>}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center' }}>
              {reach !== undefined && <span style={{ fontSize: 12, color: '#7f8c8d' }}>Notifies {reach} student{reach !== 1 ? 's' : ''}</span>}
              <button className="booking-btn-secondary" onClick={onClose} disabled={sending}>Cancel</button>
              <button className="booking-btn-primary" onClick={handleSend} disabled={!canSend}>
                {sending ? 'Sending…' : '📣 Send'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
